/**
 * AudioRecorder Component
 * 
 * High-quality audio recording component for web app
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.1: Web Audio Recorder Component - Setup
 * 
 * @example
 * ```tsx
 * <AudioRecorder
 *   config={{
 *     quality: 'high',
 *     maxDuration: 3600,
 *     autoUpload: true,
 *     onUploadComplete: (audioId) => console.log('Uploaded:', audioId)
 *   }}
 *   showWaveform
 *   showTimer
 * />
 * ```
 */

import React, { useEffect } from 'react';
import { useAudioRecorder } from './useAudioRecorder';
import { AudioRecorderProps } from './AudioRecorder.types';
import { ErrorToast } from './ErrorToast';
import styles from './AudioRecorder.module.css';

/**
 * Format duration in MM:SS format
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * AudioRecorder Component
 */
export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  config = {},
  className,
  showWaveform = true,
  showLevelMeter = true,
  showTimer = true,
  showQualitySelector = false,
  disabled = false,
  theme = 'auto',
}) => {
  const { state, controls, browserSupport } = useAudioRecorder(config);

  // Request permission on mount
  useEffect(() => {
    if (!state.hasPermission && browserSupport.mediaRecorderSupported) {
      controls.requestPermission();
    }
  }, []); // Only run once on mount

  // Handle unsupported browser
  if (!browserSupport.mediaRecorderSupported) {
    return (
      <div className={`${styles.container} ${styles.error} ${className || ''}`} data-theme={theme}>
        <div className={styles.errorMessage}>
          <span className={styles.errorIcon}>⚠️</span>
          <p>Audio recording is not supported in your browser.</p>
          <p className={styles.errorDetails}>
            Please use a modern browser like Chrome, Firefox, Safari, or Edge.
          </p>
        </div>
      </div>
    );
  }

  // Handle permission denied
  if (!state.hasPermission && state.error?.type === 'permission-denied') {
    return (
      <div className={`${styles.container} ${styles.error} ${className || ''}`} data-theme={theme}>
        <div className={styles.errorMessage}>
          <span className={styles.errorIcon}>🎤</span>
          <p>Microphone permission is required to record audio.</p>
          <button
            className={styles.permissionButton}
            onClick={controls.requestPermission}
          >
            Grant Permission
          </button>
        </div>
      </div>
    );
  }

  const isRecording = state.state === 'recording';
  const isPaused = state.state === 'paused';
  const isStopped = state.state === 'stopped';
  const isUploading = state.state === 'uploading';
  const isIdle = state.state === 'idle';

  return (
    <div
      className={`${styles.container} ${className || ''} ${disabled ? styles.disabled : ''}`}
      data-theme={theme}
    >
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>Voice Recording</h3>
        {showQualitySelector && isIdle && (
          <select className={styles.qualitySelector} disabled={disabled}>
            <option value="low">Low Quality</option>
            <option value="medium">Medium Quality</option>
            <option value="high">High Quality</option>
          </select>
        )}
      </div>

      {/* Timer */}
      {showTimer && (
        <div className={styles.timer}>
          <span 
            className={`${styles.recordingIndicator} ${isRecording ? styles.recording : ''}`}
            aria-hidden="true"
          />
          <span className={styles.duration} role="timer" aria-live="off">
            {formatDuration(state.duration)}
          </span>
        </div>
      )}

      {/* Audio Level Meter */}
      {showLevelMeter && (isRecording || isPaused) && (
        <div 
          className={styles.levelMeter}
          role="meter"
          aria-valuenow={state.audioLevel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Audio input level"
        >
          <div className={styles.levelMeterLabel} id="level-meter-label">
            Audio Level
          </div>
          <div className={styles.levelMeterBar} aria-hidden="true">
            <div
              className={styles.levelMeterFill}
              style={{ width: `${state.audioLevel}%` }}
            />
          </div>
        </div>
      )}

      {/* Waveform Visualization */}
      {showWaveform && (isRecording || isPaused) && (
        <div 
          className={styles.waveform}
          role="img"
          aria-label="Audio waveform visualization"
        >
          {/* Simplified waveform using audio level */}
          <div className={styles.waveformBars} aria-hidden="true">
            {Array.from({ length: 40 }).map((_, i) => (
              <div
                key={i}
                className={styles.waveformBar}
                style={{
                  height: `${Math.random() * state.audioLevel + 10}%`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recording Info (when stopped) */}
      {isStopped && state.recordingData && (
        <div className={styles.recordingInfo}>
          <div className={styles.recordingInfoItem}>
            <span className={styles.recordingInfoLabel}>Duration:</span>
            <span className={styles.recordingInfoValue}>
              {formatDuration(state.recordingData.duration)}
            </span>
          </div>
          <div className={styles.recordingInfoItem}>
            <span className={styles.recordingInfoLabel}>Size:</span>
            <span className={styles.recordingInfoValue}>
              {(state.recordingData.size / (1024 * 1024)).toFixed(2)} MB
            </span>
          </div>
          <div className={styles.recordingInfoItem}>
            <span className={styles.recordingInfoLabel}>Format:</span>
            <span className={styles.recordingInfoValue}>
              {state.recordingData.format.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div 
          className={styles.uploadProgress}
          role="progressbar"
          aria-valuenow={state.uploadProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Upload progress"
        >
          <div className={styles.uploadProgressLabel} id="upload-progress-label">
            Uploading...
          </div>
          <div className={styles.uploadProgressBar} aria-hidden="true">
            <div
              className={styles.uploadProgressFill}
              style={{ width: `${state.uploadProgress}%` }}
            />
          </div>
          <div className={styles.uploadProgressPercent} aria-live="polite">
            {state.uploadProgress}%
          </div>
        </div>
      )}

      {/* Error Message */}
      {state.error && state.state === 'error' && (
        <div className={styles.errorBanner}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorText}>{state.error.message}</span>
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        {isIdle && (
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={controls.startRecording}
            disabled={disabled || !state.hasPermission}
            aria-label="Start recording"
          >
            <span className={styles.buttonIcon}>🎤</span>
            <span>Start Recording</span>
          </button>
        )}

        {isRecording && (
          <>
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={controls.pauseRecording}
              disabled={disabled}
              aria-label="Pause recording"
            >
              <span className={styles.buttonIcon}>⏸</span>
              <span>Pause</span>
            </button>
            <button
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={controls.stopRecording}
              disabled={disabled}
              aria-label="Stop recording"
            >
              <span className={styles.buttonIcon}>⏹</span>
              <span>Stop</span>
            </button>
            <button
              className={`${styles.button} ${styles.buttonText}`}
              onClick={controls.cancelRecording}
              disabled={disabled}
              aria-label="Cancel recording"
            >
              Cancel
            </button>
          </>
        )}

        {isPaused && (
          <>
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={controls.resumeRecording}
              disabled={disabled}
              aria-label="Resume recording"
            >
              <span className={styles.buttonIcon}>▶️</span>
              <span>Resume</span>
            </button>
            <button
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={controls.stopRecording}
              disabled={disabled}
              aria-label="Stop recording"
            >
              <span className={styles.buttonIcon}>⏹</span>
              <span>Stop</span>
            </button>
            <button
              className={`${styles.button} ${styles.buttonText}`}
              onClick={controls.cancelRecording}
              disabled={disabled}
              aria-label="Cancel recording"
            >
              Cancel
            </button>
          </>
        )}

        {isStopped && !isUploading && (
          <>
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={controls.uploadRecording}
              disabled={disabled}
              aria-label="Upload recording"
            >
              <span className={styles.buttonIcon}>☁️</span>
              <span>Upload</span>
            </button>
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={controls.reset}
              disabled={disabled}
              aria-label="Record again"
            >
              <span className={styles.buttonIcon}>🎤</span>
              <span>Record Again</span>
            </button>
          </>
        )}

        {isUploading && (
          <button
            className={`${styles.button} ${styles.buttonText}`}
            disabled
            aria-label="Uploading"
          >
            Uploading...
          </button>
        )}
      </div>

      {/* Browser Info (Debug) */}
      {process.env.NODE_ENV === 'development' && (
        <details className={styles.debug}>
          <summary>Debug Info</summary>
          <pre>{JSON.stringify({ state: state.state, browserSupport }, null, 2)}</pre>
        </details>
      )}

      {/* Error Toast (Enhanced Error Handling - Task 2.8) */}
      <ErrorToast
        error={state.errorDetails || null}
        onDismiss={controls.reset}
        autoDismiss={5000}
        showSuggestions={true}
        onRetry={controls.startRecording}
      />
    </div>
  );
};

export default AudioRecorder;

